// src/lib/combos.ts
import { supabase } from '../services/supabaseClient';

export type ComboStatus = 'draft' | 'pending_acceptance' | 'active' | 'paused' | 'sold_out' | 'cancelled';
export type ComboParticipantStatus = 'invited' | 'accepted' | 'declined' | 'counter_offer';

export type ComboInput = {
  title: string;
  description: string;
  region?: string;
  city?: string;
  category?: string;
  creator_business_id?: string;
  merchant_value?: number;
  suggested_price?: number;
  capacity?: number;
  valid_from?: string | null;
  valid_until?: string | null;
  image_url?: string;
  conditions?: string;
  invited_business_ids?: string[];
  time_slots?: Array<{ date: string; start_time: string; end_time: string; capacity?: number }>;
};

const clean = (v: any) => (typeof v === 'string' ? v.trim() : v);

export async function comboAction<T = any>(action: string, payload: Record<string, any> = {}) {
  const { data, error } = await supabase.functions.invoke('combos', {
    body: { action, ...payload },
  });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error || 'Erreur Forfeo Combos');
  return data as T;
}

export async function adminComboAction<T = any>(action: string, payload: Record<string, any> = {}) {
  const { data, error } = await supabase.functions.invoke('admin-combos', {
    body: { action, ...payload },
  });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error || 'Erreur admin Combos');
  return data as T;
}

export async function invitationComboAction<T = any>(action: string, payload: Record<string, any> = {}) {
  const { data, error } = await supabase.functions.invoke('combo-invitations', {
    body: { action, ...payload },
  });
  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error || 'Erreur invitation Combo');
  return data as T;
}

export function buildForfeoComComboUrl(combo: any, slot?: any) {
  const base = import.meta.env.VITE_FORFEO_COM_URL || 'https://forfeo.com';
  const params = new URLSearchParams();
  if (combo?.id) params.set('combo_id', combo.id);
  if (combo?.slug) params.set('combo_slug', combo.slug);
  if (slot?.id) params.set('slot_id', slot.id);
  if (slot?.date) params.set('date', slot.date);
  if (slot?.start_time) params.set('time', slot.start_time);
  params.set('source', 'forfeo_explore');
  params.set('utm_source', 'forfeoexplore');
  params.set('utm_medium', 'combo');
  params.set('utm_campaign', 'combos_forfeo');
  return `${base.replace(/\/$/, '')}/combo/${encodeURIComponent(combo?.slug || combo?.id || 'forfeo')}?${params.toString()}`;
}

export function calculateComboPricing(values: Array<number | string | null | undefined>, commissionRate = 8) {
  const totalValue = values.reduce((sum, v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? sum + n : sum;
  }, 0);
  const suggestedDiscountRate = totalValue >= 300 ? 0.12 : 0.08;
  const suggestedPrice = Math.max(0, Math.round(totalValue * (1 - suggestedDiscountRate)));
  const commissionAmount = Math.round((suggestedPrice * commissionRate / 100) * 100) / 100;
  const netPartners = Math.round((suggestedPrice - commissionAmount) * 100) / 100;
  return { totalValue, suggestedPrice, suggestedDiscountRate, commissionRate, commissionAmount, netPartners };
}

export function formatMoney(value: any) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(n);
}

export function normalizeComboPayload(input: ComboInput) {
  return {
    ...input,
    title: clean(input.title),
    description: clean(input.description),
    region: clean(input.region),
    city: clean(input.city),
    category: clean(input.category),
    conditions: clean(input.conditions),
    image_url: clean(input.image_url),
  };
}
